import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { WiDaySunny, WiCloudy, WiDayCloudy } from 'react-icons/wi';
import { MdWbSunny } from 'react-icons/md';
import { FaTools, FaSearch, FaCalendarAlt, FaCheck, FaGlobe, FaBuilding } from 'react-icons/fa';
import { calendarAPI, authAPI } from '../api/api';
import WeatherWidget from '../components/WeatherWidget';
import '../css/Appointments.css';

const Appointments = ({ isSidebarClosed, setIsSidebarClosed }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmEvent, setConfirmEvent] = useState(null);
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  const months = [
    'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
    'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
  ];

  const daysOfWeek = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];
  const daysOfWeekShort = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

  const fetchEvents = async (startDate, endDate) => {
    try {
      setLoading(true);
      setError(null);
      
      const currentUser = authAPI.getCurrentUser();
      let clientId = null;
      
      if (currentUser && (currentUser.client_id || currentUser.company_id)) {
        clientId = currentUser.client_id || currentUser.company_id;
      }
      
      const eventsData = await calendarAPI.getCalendarEvents(
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
        clientId
      );
      
      const groupedEvents = {};
      eventsData.events.forEach(event => {
        const dateKey = event.start_date;
        if (!groupedEvents[dateKey]) {
          groupedEvents[dateKey] = [];
        }
        
        let eventType = 'general';
        let icon = <FaCalendarAlt />;
        let status = 'active';
        
        if (event.is_bookable) {
          if (event.booking_status === 'booked') {
            eventType = 'booked';
            icon = <FaCheck />;
            status = 'booked';
          } else {
            eventType = 'available';
            icon = <FaCalendarAlt />;
            status = 'available';
          }
        } else if (event.is_global) {
          eventType = 'global';
          icon = <FaGlobe />;
          status = 'global';
        } else if (event.client) {
          eventType = 'client';
          icon = <FaBuilding />;
          status = 'client';
        }
        
        let timeDisplay = '';
        if (event.all_day) {
          timeDisplay = 'Egész nap';
        } else if (event.start_time && event.end_time) {
          const startTime = event.start_time.substring(0, 5);
          const endTime = event.end_time.substring(0, 5);
          timeDisplay = `${startTime}-${endTime}`;
        }
        
        groupedEvents[dateKey].push({
          id: event.id,
          time: timeDisplay,
          title: event.title,
          description: event.description,
          type: eventType,
          icon: icon,
          status: status,
          isBookable: event.is_bookable,
          isGlobal: event.is_global,
          client: event.client,
          startTime: event.start_time,
          endTime: event.end_time,
          allDay: event.all_day,
          color: event.color,
          category: event.category,
          location: event.location,
          bookingStatus: event.booking_status,
          bookingId: event.booking_id
        });
      });
      
      setEvents(groupedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const bookAppointment = async (eventId, dateKey, startTime) => {
    try {
      setLoading(true);
      const currentUser = authAPI.getCurrentUser();
      
      await calendarAPI.bookTimeSlot(
        eventId,
        dateKey,
        startTime,
        currentUser ? (currentUser.user_id || currentUser.id) : null
      );
      
      const startDate = new Date(currentDate);
      startDate.setDate(1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      await fetchEvents(startDate, endDate);
      
      setShowConfirmDialog(false);
      setConfirmEvent(null);
    } catch (err) {
      console.error('Error booking appointment:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelAppointment = async (eventId, dateKey, startTime) => {
    try {
      setLoading(true);
      
      await calendarAPI.cancelTimeSlot(eventId, dateKey, startTime);
      
      const startDate = new Date(currentDate);
      startDate.setDate(1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      await fetchEvents(startDate, endDate);
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1);
    
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const firstDayOfWeek = firstDayOfMonth.getDay();
    const adjustedFirstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
 
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - adjustedFirstDayOfWeek);
    
    const days = [];
    const currentDate = new Date(startDate);
  
    while (days.length < 42) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;
    
    return dateKey;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const hasEvents = (date) => {
    const dateKey = formatDateKey(date);
    if (!events[dateKey]) return false;
    
    // If there's a booked appointment, only show that one
    const hasBookedAppointment = events[dateKey].some(event => 
      event.isBookable && event.bookingStatus === 'booked'
    );
    
    if (hasBookedAppointment) {
      // Only show events if there are booked appointments or non-bookable events
      return events[dateKey].some(event => 
        event.bookingStatus === 'booked' || !event.isBookable
      );
    }
    
    // If no booked appointments, show all events
    return events[dateKey].length > 0;
  };

  const getEventTypes = (date) => {
    const dateKey = formatDateKey(date);
    if (!events[dateKey]) return [];
    
    // If there's a booked appointment, only show booked and non-bookable event types
    const hasBookedAppointment = events[dateKey].some(event => 
      event.isBookable && event.bookingStatus === 'booked'
    );
    
    if (hasBookedAppointment) {
      const visibleEvents = events[dateKey].filter(event => 
        event.bookingStatus === 'booked' || !event.isBookable
      );
      return [...new Set(visibleEvents.map(event => event.type))];
    }
    
    return [...new Set(events[dateKey].map(event => event.type))];
  };

  const getSelectedDateEvents = () => {
    const dateKey = formatDateKey(selectedDate);
    if (!events[dateKey]) return [];
    
    // If there's a booked appointment, only show booked appointments and non-bookable events
    const hasBookedAppointment = events[dateKey].some(event => 
      event.isBookable && event.bookingStatus === 'booked'
    );
    
    if (hasBookedAppointment) {
      return events[dateKey].filter(event => 
        event.bookingStatus === 'booked' || !event.isBookable
      );
    }
    
    // If no booked appointments, show all events
    return events[dateKey];
  };

  const handleBookClick = (event) => {
    setConfirmEvent(event);
    setShowConfirmDialog(true);
  };

  const confirmBooking = () => {
    if (confirmEvent) {
      bookAppointment(confirmEvent.id, formatDateKey(selectedDate), confirmEvent.startTime);
    }
  };

  const cancelBooking = () => {
    setShowConfirmDialog(false);
    setConfirmEvent(null);
  };

  const changeMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const changeYear = (year) => {
    if (year >= 2020 && year <= 2030) {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setFullYear(year);
        return newDate;
      });
    }
  };

  const changeMonthDirect = (monthIndex) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(monthIndex);
      return newDate;
    });
    setShowDatePicker(false);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };



  useEffect(() => {
    const startDate = new Date(currentDate);
    startDate.setDate(1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    fetchEvents(startDate, endDate);
  }, [currentDate]);


  useEffect(() => {
    const user = authAPI.getCurrentUser();
    setCurrentUser(user);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);



  if (loading && Object.keys(events).length === 0) {
    return (
      <div>
        <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
        <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
        <div className="page-content">
          <div className="appointments-container">
            <h2>Időpontok / Határidők</h2>
            <div className="loading">Betöltés...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
      <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
      <div className="page-content">
        <div className="appointments-container">
          <h2>Időpontok / Határidők</h2>
          
          {error && (
            <div className="error-message">
              Hiba: {error}
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}
          
          <div className="calendar-container">
            {/* Calendar Section */}
            <div className="calendar-section">
              {/* Calendar Header */}
              <div className="calendar-header">
                <div className="month-navigation">
                  <button onClick={() => changeMonth(-1)} className="nav-btn" disabled={loading}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6"/>
                    </svg>
                  </button>
                  <div className="date-picker-container">
                    <h3 className="month-title" onClick={toggleDatePicker}>
                      {months[currentDate.getMonth()]} <span className="year">{currentDate.getFullYear()}</span>
                    </h3>
                    {showDatePicker && (
                      <div className="date-picker-dropdown">
                        <div className="picker-section">
                          <h4>Hónap</h4>
                          <div className="picker-grid">
                            {months.map((month, index) => (
                              <button
                                key={month}
                                className={`picker-item ${index === currentDate.getMonth() ? 'active' : ''}`}
                                onClick={() => changeMonthDirect(index)}
                              >
                                {month}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="picker-section">
                          <h4>Év</h4>
                          <div className="year-input-container">
                            <div className="year-spinner">
                              <button 
                                className="year-btn" 
                                onClick={() => changeYear(currentDate.getFullYear() - 1)}
                                disabled={currentDate.getFullYear() <= 2020}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M18 15l-6-6-6 6"/>
                                </svg>
                              </button>
                              <input
                                type="number"
                                value={currentDate.getFullYear()}
                                onChange={(e) => changeYear(parseInt(e.target.value))}
                                className="year-input"
                                min="2020"
                                max="2030"
                              />
                              <button 
                                className="year-btn" 
                                onClick={() => changeYear(currentDate.getFullYear() + 1)}
                                disabled={currentDate.getFullYear() >= 2030}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M6 9l6 6 6-6"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button onClick={() => changeMonth(1)} className="nav-btn" disabled={loading}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="calendar-grid">
                {/* Day headers */}
                <div className="calendar-days-header">
                  {daysOfWeek.map((day, index) => (
                    <div key={index} className="day-headers">
                      <span className="day-full">{day}</span>
                      <span className="day-short">{daysOfWeekShort[index]}</span>
                    </div>
                  ))}
                </div>

                {/* Calendar dates */}
                <div className="calendar-dates">
                  {getDaysInMonth(currentDate).map((date, index) => {
                    const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                    const isCurrentDay = isToday(date);
                    const isSelectedDay = isSelected(date);
                    const hasEventsForDay = hasEvents(date);
                    const eventTypes = getEventTypes(date);

                    return (
                      <div
                        key={index}
                        className={`calendar-date ${isCurrentMonth ? 'current-month' : 'other-month'} 
                                  ${isCurrentDay ? 'today' : ''} 
                                  ${isSelectedDay ? 'selected' : ''}
                                  ${hasEventsForDay ? 'has-events' : ''}`}
                        onClick={() => handleDateClick(date)}
                      >
                        <span className="date-number">{date.getDate()}</span>
                        {hasEventsForDay && (
                          <div className="event-indicators">
                            {eventTypes.slice(0, 3).map((type, idx) => (
                              <div key={idx} className={`event-dot event-${type}`}></div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Events Section */}
            <div className="events-section">
              {/* Selected Date Details */}
              <div className="selected-date-details">
                <div className="date-header">
                  <div className="selected-date-info">
                    <span className="date-label">
                      {selectedDate.getFullYear()}/{String(selectedDate.getMonth() + 1).padStart(2, '0')}/{String(selectedDate.getDate()).padStart(2, '0')}
                    </span>
                    <div className="weather-info">
                      <WeatherWidget compact={true} />
                    </div>
                  </div>
                </div>

                {/* Main Event Highlight */}
                {getSelectedDateEvents().length > 0 && (
                  <div className="main-event-highlight">
                    {getSelectedDateEvents()[0].title}
                  </div>
                )}

                {/* Events List */}
                <div className="events-list">
                  {loading ? (
                    <div className="loading">Betöltés...</div>
                  ) : (
                    <>
                      {getSelectedDateEvents().map(event => (
                        <div key={event.id} className={`event-item event-${event.status}`}>
                          <div className="event-time">{event.time}</div>
                          <div className="event-content">
                            <span className="event-icon">{event.icon}</span>
                            <div className="event-details">
                              <span className="event-title">{event.title}</span>
                              {event.description && (
                                <span className="event-description">{event.description}</span>
                              )}
                              {event.client && (
                                <span className="event-client">Cég: {event.client.name}</span>
                              )}
                              {event.isGlobal && (
                                <span className="event-type">Globális esemény</span>
                              )}
                            </div>
                          </div>
                          {event.isBookable && event.bookingStatus === 'open' && (
                            <button 
                              className="book-btn"
                              onClick={() => handleBookClick(event)}
                              disabled={loading}
                            >
                              Befoglalás
                            </button>
                          )}
                          {event.isBookable && event.bookingStatus === 'booked' && (
                            <button 
                              className="cancel-btn"
                              onClick={() => cancelAppointment(event.id, formatDateKey(selectedDate), event.startTime)}
                              disabled={loading}
                            >
                              Lemondás
                            </button>
                          )}
                        </div>
                      ))}
                      {getSelectedDateEvents().length === 0 && (
                        <div className="no-events">Nincsenek események erre a napra</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="confirm-dialog-overlay">
          <div className="confirm-dialog">
            <div className="confirm-dialog-header">
              <h3>Időpont befoglalása</h3>
            </div>
            <div className="confirm-dialog-content">
              <p>Biztosan befoglalod ezt az időpontot?</p>
              <div className="confirm-event-details">
                <span className="confirm-event-time">{confirmEvent?.time}</span>
                <span className="confirm-event-title">{confirmEvent?.title}</span>
                {confirmEvent?.description && (
                  <span className="confirm-event-description">{confirmEvent.description}</span>
                )}
              </div>
            </div>
            <div className="confirm-dialog-actions">
              <button className="confirm-btn-cancel" onClick={cancelBooking}>
                Mégse
              </button>
              <button className="confirm-btn-confirm" onClick={confirmBooking} disabled={loading}>
                Befoglalás
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments; 